import { useState } from "react";
import { ArrowLeft } from "lucide-react";
import "bootstrap/dist/css/bootstrap.min.css";
import "../Assets/css/influencer.css";
import { useNavigate } from "react-router-dom";

export default function BankAccountSetup() {
  const [currentStep, setCurrentStep] = useState("selection");
  const [bankDetails, setBankDetails] = useState({
    accountHolderName: "",
    bankName: "",
    accountNumber: "",
    confirmAccountNumber: "",
    ifscCode: "",
    branchName: "",
  });
  const [upiHandle, setUpiHandle] = useState("");
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const handleBankChange = (e) => {
    const { name, value } = e.target;
    setBankDetails({ ...bankDetails, [name]: value });
  };

  const validateBankDetails = () => {
    const errs = {};

    // Account Holder Name
    if (!bankDetails.accountHolderName.trim()) {
      errs.accountHolderName = "Account holder name is required";
    } else if (!/^[A-Za-z\s]{3,}$/.test(bankDetails.accountHolderName)) {
      errs.accountHolderName = "Only letters & spaces, min 3 characters";
    }

    // Bank Name
    if (!bankDetails.bankName.trim()) {
      errs.bankName = "Bank name is required";
    } else if (bankDetails.bankName.length < 2) {
      errs.bankName = "Bank name must be at least 2 characters";
    }

    // Account Number
    if (!bankDetails.accountNumber.trim()) {
      errs.accountNumber = "Account number is required";
    } else if (!/^\d{9,18}$/.test(bankDetails.accountNumber)) {
      errs.accountNumber = "Must be 9 to 18 digits only";
    }

    // Confirm Account Number
    if (!bankDetails.confirmAccountNumber.trim()) {
      errs.confirmAccountNumber = "Please confirm account number";
    } else if (bankDetails.accountNumber !== bankDetails.confirmAccountNumber) {
      errs.confirmAccountNumber = "Account numbers do not match";
    }

    // IFSC Code
    if (!bankDetails.ifscCode.trim()) {
      errs.ifscCode = "IFSC code is required";
    } else if (
      !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(bankDetails.ifscCode.toUpperCase())
    ) {
      errs.ifscCode = "Invalid IFSC code format";
    }

    return errs;
  };

  const validateUpi = () => {
    const errs = {};
    if (!upiHandle.trim()) {
      errs.upiHandle = "UPI handle is required";
    } else if (!/^[\w.-]{2,}@[a-zA-Z]{2,}$/.test(upiHandle)) {
      errs.upiHandle = "Invalid UPI handle (e.g., yourname@bank)";
    }
    return errs;
  };

  const handleSave = () => {
    let validationErrors = {};
    if (currentStep === "bank-details") {
      validationErrors = validateBankDetails();
    } else if (currentStep === "upi-handle") {
      validationErrors = validateUpi();
    }

    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      alert("Saved successfully!");
      // Add your API call here if needed
    }
  };

  return (
    <div className="min-vh-100 payment-methods-page p-4">
      <div className="container max-w-2xl mx-auto">
        <button
          onClick={() => navigate("/payment")}
          className="btn btn-light mb-3 d-flex align-items-center"
        >
          <ArrowLeft size={20} className="me-2" />
          Back
        </button>

        <div className="text-center">
          <div className="row">
            <div className="col-12">
              {currentStep !== "bank-details" ? (
                <div
                  onClick={() => setCurrentStep("bank-details")}
                  className="card p-4 mb-3 shadow-sm cursor-pointer"
                >
                  <div className="payment-method-card-body">
                    <div className="border d-inline-flex align-items-center justify-content-center p-3 mb-2">
                      <span className="upi-text fs-4">+</span>
                    </div>
                    <p className="mb-0 add-bank-text">Add Bank Account</p>
                  </div>
                </div>
              ) : (
                <form
                  className="p-4 payment-method-card"
                  onSubmit={(e) => e.preventDefault()}
                >
                  <h2 className="text-bank-detail mb-4 text-start">
                    Bank Details
                  </h2>
                  <div className="row g-3 text-start">
                    {[
                      {
                        label: "Account Holder Name",
                        name: "accountHolderName",
                        placeholder: "Name",
                      },
                      {
                        label: "Bank Name",
                        name: "bankName",
                        placeholder: "Bank Name",
                      },
                      {
                        label: "Account Number",
                        name: "accountNumber",
                        placeholder: "Acc. Number",
                      },
                      {
                        label: "Confirm Account Number",
                        name: "confirmAccountNumber",
                        placeholder: "Confirm Acc. Number",
                      },
                      {
                        label: "IFSC Code (India)",
                        name: "ifscCode",
                        placeholder: "11 Digit IFSC Code",
                      },
                      {
                        label: "Branch Name (Optional)",
                        name: "branchName",
                        placeholder: "Branch Name",
                      },
                    ].map((field, idx) => (
                      <div className="col-md-6" key={idx}>
                        <label className="form-label">{field.label}</label>
                        <input
                          type="text"
                          name={field.name}
                          className={`form-control ${
                            errors[field.name] ? "is-invalid" : ""
                          }`}
                          placeholder={field.placeholder}
                          value={bankDetails[field.name]}
                          onChange={handleBankChange}
                        />
                        {errors[field.name] && (
                          <div className="invalid-feedback">
                            {errors[field.name]}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </form>
              )}
            </div>

            <div className="col-12 d-flex align-items-center my-3">
              <div className="flex-grow-1 border-bottom"></div>
              <span className="mx-2 text-or fw-bold">OR</span>
              <div className="flex-grow-1 border-bottom"></div>
            </div>

            <div className="col-12">
              {currentStep !== "upi-handle" ? (
                <div
                  onClick={() => setCurrentStep("upi-handle")}
                  className="card p-4 shadow-sm cursor-pointer"
                >
                  <div className="payment-method-card-body">
                    <div className="border d-inline-flex align-items-center justify-content-center p-3 mb-2">
                      <span className="upi-text fs-4">+</span>
                    </div>
                    <p className="mb-0 add-bank-text">Add UPI Handle</p>
                  </div>
                </div>
              ) : (
                <form
                  className="p-4 payment-method-card"
                  onSubmit={(e) => e.preventDefault()}
                >
                  <h2 className="upi-text mb-4 text-start">UPI Handle</h2>
                  <div className="d-flex mb-3 col-6">
                    <input
                      type="text"
                      className={`form-control me-2 ${
                        errors.upiHandle ? "is-invalid" : ""
                      }`}
                      placeholder="e.g. yourname@bank"
                      value={upiHandle}
                      onChange={(e) => setUpiHandle(e.target.value)}
                    />
                    <div className="btn payment-verify">Verify</div>
                    {errors.upiHandle && (
                      <div className="invalid-feedback d-block ms-2">
                        {errors.upiHandle}
                      </div>
                    )}
                  </div>
                </form>
              )}
            </div>
          </div>

          <div className="d-flex justify-content-end mt-4">
            <button
              type="button"
              className="btn btn-outline-secondary me-2"
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
